import {
  HomeIcon,
  BoltIcon,
  ChartPieIcon,
  ChartBarIcon,
  BanknotesIcon,
  ArrowsRightLeftIcon,
  WalletIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  LinkIcon,
  ListBulletIcon,
  Cog6ToothIcon,
  GiftIcon,
  LifebuoyIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { FEATURE_FLAG_KEYS } from "@moonscape/shared";

// Define the navigation item type
export interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  featureFlag?: string; // Optional feature flag key
  roles?: string[]; // Optional roles for role-based access
}

// Navigation configuration split into main (top) and bottom sections
export interface NavigationConfig {
  main: NavigationItem[];
  bottom: NavigationItem[];
}

/**
 * Main navigation items (displayed at the top)
 */
const mainNavigation: NavigationItem[] = [
  { name: "Quickstart", href: "/quickstart", icon: BoltIcon },
  { name: "My Clients", href: "/a/clients", icon: UsersIcon, roles: ["accountant"] },
  { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
  { name: "Transactions", href: "/transactions", icon: ListBulletIcon },
  { name: "Portfolio", href: "/portfolio", icon: ChartBarIcon },
  { name: "Integrations", href: "/integrations", icon: LinkIcon },
  { name: "Tax Center", href: "/tax", icon: ChartPieIcon },
  { name: "Reports", href: "/tax/reports", icon: DocumentTextIcon },
  { name: "Tax Harvesting", href: "/tax/loss-harvesting", icon: BanknotesIcon },
  {
    name: "Tax Safe Harbor",
    href: "/tax/safe-harbor",
    icon: ChartPieIcon,
    featureFlag: FEATURE_FLAG_KEYS.WEB_SAFE_HARBOR,
  },
  { name: "Transaction Audit", href: "/tax/audit", icon: ClipboardDocumentCheckIcon },
  { name: "Bridge Transfers", href: "/bridge-matches", icon: ArrowsRightLeftIcon },
];

/**
 * Bottom navigation items (displayed at the bottom of sidebar)
 */
const bottomNavigation: NavigationItem[] = [
  {
    name: "Refer a friend",
    href: "/settings/refer",
    icon: GiftIcon,
    featureFlag: FEATURE_FLAG_KEYS.WEB_SHOW_IAP,
  },
  { name: "Settings", href: "/settings/profile", icon: Cog6ToothIcon },
  { name: "Support", href: "/support", icon: LifebuoyIcon },
];

const onPath = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(href + "/");

/**
 * Is `href` the nav entry that should light up for `pathname`?
 * Prefix match, longest wins — so /tax/audit lights "Transaction Audit", not "Tax Center".
 * `siblings` is every href competing for the highlight.
 */
export const isNavActive = (pathname: string, href: string, siblings: string[]): boolean =>
  onPath(pathname, href) &&
  !siblings.some((s) => s.length > href.length && onPath(pathname, s));

// Helper function to filter navigation items
const filterNavigationItems = (
  items: NavigationItem[],
  featureFlags: Record<string, boolean> = {},
  userRoles: string[] = []
): NavigationItem[] => {
  return items.filter((item) => {
    // Check feature flags
    if (item.featureFlag && !featureFlags[item.featureFlag]) {
      return false;
    }

    // Check roles if needed
    if (item.roles && !item.roles.some((role) => userRoles.includes(role))) {
      return false;
    }

    return true;
  });
};

/**
 * Navigation builder function - returns split navigation config
 */
export const buildNavigation = (
  featureFlags: Record<string, boolean> = {},
  userRoles: string[] = []
): NavigationConfig => {
  return {
    main: filterNavigationItems(mainNavigation, featureFlags, userRoles),
    bottom: filterNavigationItems(bottomNavigation, featureFlags, userRoles),
  };
};

/**
 * Legacy flat navigation builder for backward compatibility
 * Combines main and bottom sections
 */
export const buildFlatNavigation = (
  featureFlags: Record<string, boolean> = {},
  userRoles: string[] = []
): NavigationItem[] => {
  const config = buildNavigation(featureFlags, userRoles);
  return [...config.main, ...config.bottom];
};

export default mainNavigation;
